import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Verification from './pages/Verification'
import Results from './pages/Results'
import Admin from './pages/Admin'
import Settings from './pages/Settings'
import TokenSetup from './components/TokenSetup'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ActionProvider } from './contexts/ActionContext'
import api, { API_BASE_URL } from './config/api'
import LeadManagement from './pages/LeadManagement'
import ListVerification from './pages/ListVerification'
import Landing from './pages/Landing'
import Installer from './pages/Installer'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import DownloadLists from './pages/DownloadLists';
import Profile from './pages/Profile';
import WorkerError from './pages/WorkerError';
import Updates from './pages/Updates';
import Satellites from './pages/Satellites';
import Modules from './pages/Modules';
import DeveloperAPI from './pages/DeveloperAPI';

function App() {
  return (
    <AuthProvider>
      <ActionProvider>
        <AppContent />
      </ActionProvider>
    </AuthProvider>
  )
}

function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return null
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />
  return children
}

function AppContent() {
  const [isConfigured, setIsConfigured] = useState(null)
  const [licenseError, setLicenseError] = useState(null)
  const [appVersion, setAppVersion] = useState('')
  const { isAuthenticated, isLoading } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/admin/updates/info')
        .then(res => {
          if (res.data.success) {
            setAppVersion(res.data.currentVersion)
          }
        })
        .catch(err => console.error('Failed to fetch version', err))
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetch('/api/setup/status', {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setIsConfigured(!!data.isConfigured))
      .catch(() => setIsConfigured(false))
  }, [])

  // Worker status check effect - only run if configured and authenticated
  useEffect(() => {
    if (isConfigured && isAuthenticated && !isLoading) {
      const checkWorkerStatus = async () => {
        try {
          // Check worker status using the public endpoint
          const response = await fetch('/api/worker-status', {
            credentials: 'include'
          });

          if (!response.ok) {
            // Error checking worker status
            setLicenseError({
              code: 'WORKER_NOT_RUNNING',
              error: 'Cannot check worker status',
              details: 'Failed to check if verification worker is running'
            });
            return;
          }

          const data = await response.json();

          if (!data.data.worker_running) {
            // Worker is not running
            setLicenseError({
              code: 'WORKER_NOT_RUNNING',
              error: 'Verification worker is not running',
              details: data.data.reason || 'The background verification process has stopped'
            });
            return;
          }

          // If we get here, worker is running
          setLicenseError(null);
        } catch (error) {
          // no-op
          // Set error if we can't reach the worker status endpoint
          setLicenseError({
            code: 'WORKER_NOT_RUNNING',
            error: 'Cannot connect to worker status service',
            details: error.message
          });
        }
      };

      // Check worker status immediately
      checkWorkerStatus();

      // Set up periodic worker status checks every 30 seconds
      const interval = setInterval(checkWorkerStatus, 30000);

      return () => clearInterval(interval);
    }
  }, [isConfigured, isAuthenticated, isLoading]);

  // Show worker error if present
  if (licenseError) {
    return <WorkerError errorData={licenseError} />;
  }

  if (isLoading || isConfigured === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="flex flex-col items-center gap-4">
            <span className="inline-block h-16 w-16 rounded-full shadow-lg bg-blue-100 dark:bg-gray-800 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24" rx="12" fill="#2563eb" />
                <path d="M7 9l5 4 5-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="4" y="7" width="16" height="10" rx="2" stroke="#fff" strokeWidth="2" />
              </svg>
            </span>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading Smart Email Verifier...</p>
        </div>
      </div>
    )
  }

  // Show installer if not configured
  if (!isConfigured) {
    return (
      <Routes>
        <Route path="/setup" element={<Installer />} />
        <Route path="*" element={<Installer />} />
      </Routes>
    )
  }

  // Show landing page if not authenticated (and not in setup mode)
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Layout>
        <Routes>
          <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/verification" element={<RequireAuth><Verification /></RequireAuth>} />
          <Route path="/results" element={<RequireAuth><Results /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
          <Route path="/satellites" element={<RequireAuth><Satellites /></RequireAuth>} />
          <Route path="/modules" element={<RequireAuth><Modules /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
          <Route path="/api-access" element={<RequireAuth><DeveloperAPI /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/leads" element={<RequireAuth><LeadManagement /></RequireAuth>} />
          <Route path="/list-verification" element={<RequireAuth><ListVerification /></RequireAuth>} />
          <Route path="/download-lists" element={<RequireAuth><DownloadLists /></RequireAuth>} />
          <Route path="/updates" element={<RequireAuth><Updates /></RequireAuth>} />
          <Route path="*" element={<RequireAuth><Dashboard /></RequireAuth>} />
        </Routes>
      </Layout>
      {/* Footer */}
      <footer className="w-full mt-auto py-4 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-800 text-center text-xs text-gray-500 dark:text-gray-400">
        <span>&copy; {new Date().getFullYear()} UnCutSolutions. All rights reserved. | Developed by Abdul Wasay Mughal {appVersion && `| v${appVersion}`}</span>
      </footer>
    </div>
  )
}

export default App 